import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'goodtime.playerName';

/** Same in-tab pub/sub pattern as {@link usePlayerAvatar} so a write
 * from one hook instance reaches every other live instance without
 * needing the components to remount. Cross-tab sync is handled by the
 * native `storage` event. */
type Listener = (next: string | null) => void;
const listeners = new Set<Listener>();
function emit(next: string | null): void {
  for (const fn of listeners) fn(next);
}

function readStored(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Player display name — short string the player picks during the
 * onboarding "Pick your look" step alongside the avatar. Persisted to
 * localStorage; reads any time another component (cast strip,
 * leaderboard, profile screen) needs to show "you" by name.
 *
 * <p>v1 stores client-side only. When social profiles ship the name
 * moves server-side and this hook becomes a thin wrapper around the
 * server-fetched profile. Until then the auto-generated "Player be15"
 * placeholder is replaced by whatever the player typed.
 *
 * @returns tuple of [currentName, setCurrentName]. Setting null
 *   clears the persisted name (the consumer falls back to the
 *   auto-generated placeholder).
 */
export function usePlayerName(): [string | null, (name: string | null) => void] {
  const [name, setName] = useState<string | null>(() => readStored());

  useEffect(() => {
    listeners.add(setName);
    if (typeof window === 'undefined') return () => {
      listeners.delete(setName);
    };
    const handler = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) return;
      setName(event.newValue);
    };
    window.addEventListener('storage', handler);
    return () => {
      listeners.delete(setName);
      window.removeEventListener('storage', handler);
    };
  }, []);

  const update = useCallback((next: string | null) => {
    if (typeof window !== 'undefined') {
      try {
        if (next === null || next.trim() === '') {
          window.localStorage.removeItem(STORAGE_KEY);
        } else {
          window.localStorage.setItem(STORAGE_KEY, next.trim());
        }
      } catch {
        // Quota / private mode — ignore.
      }
    }
    emit(next === null || next.trim() === '' ? null : next.trim());
  }, []);

  return [name, update];
}
